// ═══════════════════════════════════════════════════════════════
// V·IANet Governance — Document Styles Configuration
// FUENTE ÚNICA DE VERDAD para estilos de documentos normativos
// ═══════════════════════════════════════════════════════════════

// ─── Page (A4) dimensions in px (96 DPI) ───
export const PAGE = {
  width: 794, // 210mm at 96dpi
  height: 1123, // 297mm at 96dpi
  padTop: 60,
  padBottom: 60,
  padSide: 64,
  headerHeight: 40,
  footerHeight: 30,
  contentPadTop: 12, // padding inside content area (below header) — ~1 line
  contentPadBot: 0, // no bottom padding — pagination handles spacing
  get contentHeight() {
    return (
      this.height -
      this.padTop -
      this.headerHeight -
      this.padBottom -
      this.footerHeight -
      this.contentPadTop -
      this.contentPadBot
    );
  },
} as const;

// ─── Font stacks ───
export const FONTS = {
  normative: "Calibri, 'Segoe UI', Arial, sans-serif",
  ui: 'Inter, system-ui, -apple-system, sans-serif',
  mono: "'Cascadia Code', 'Fira Code', monospace",
} as const;

// ─── Normative colors ───
export const DOC_COLORS = {
  // Heading colors (match corporate Word templates)
  h1: '#1F3864', // Azul oscuro nivel 1
  h2: '#2F5496', // Azul medio nivel 2
  h3: '#4472C4', // Azul claro nivel 3
  h4: '#4472C4', // Same as h3

  // Body
  body: '#000000',
  bodySecondary: '#595959',

  // Table (match PE.21.HE.01 script: TH=0E243E, TR2=F2F4F6)
  tableHeaderBg: '#0E243E',
  tableHeaderBgAlt: '#2F5496',
  tableHeaderText: '#FFFFFF',
  tableBorder: '#D9D9D9',
  tableAltRowBg: '#F2F4F6',

  // Additional palette from PE.21.HE.01
  azulClaro: '#A8C4E8', // AZC
  acentoSuave: '#E8EFF9', // ACS
  grisClaro: '#F2F2F2', // GC (celdas etiqueta)

  // Borders & decorations
  h1Border: '#D6E4F0',
  headerBorderBottom: '#D6E4F0',
  footerBorderTop: '#D9D9D9',
  hr: '#D9D9D9',

  // Callout / blockquote
  calloutBorder: '#0070C0',
  calloutBg: '#EDF2F8',
  calloutText: '#0070C0',

  // Character styles
  boldItalicColor: '#1F3864',
  blueChar: '#2F5496',
  redChar: '#DC2626',

  // Page header/footer text
  pageHeaderMuted: '#8B92A5',
  pageHeaderAccent: '#B4C6E7',
} as const;

// ─── Paragraph styles ───
export interface ParagraphStyle {
  fontFamily: string;
  fontSize: string;
  fontWeight: number;
  color: string;
  lineHeight: number;
  textAlign?: string;
  textIndent?: string;
  marginTop: number;
  marginBottom: number;
  borderBottom?: string;
  paddingBottom?: number;
  paddingLeft?: number;
  listStyleType?: string;
  fontStyle?: string;
}

// Values extracted from Word styles.xml (consistent across all templates):
//   w:sz = half-points → pt = sz/2
//   w:spacing w:line: 240=single(1.0), 360=1.5×
//   w:spacing w:before/after: twips → pt = val/20
//   w:ind w:firstLine: twips → cm ≈ val/567

export const PARAGRAPH_STYLES: Record<string, ParagraphStyle> = {
  Titulo1: {
    fontFamily: FONTS.normative,
    fontSize: '12pt', // Word: sz=24 → 12pt
    fontWeight: 400, // Word: no bold — caps white on shaded bg
    color: '#FFFFFF', // Word: white text on #1F3864 background
    lineHeight: 1.0, // Word: line=240 (single)
    marginTop: 24,
    marginBottom: 4,
    // MO.03: w:shd fill="1F3864" + w:caps — full background shading, uppercase
  },
  Titulo2: {
    fontFamily: FONTS.normative,
    fontSize: '11pt', // Word: sz=22 → 11pt
    fontWeight: 700, // Word: w:b
    color: DOC_COLORS.body, // Word: no explicit color — inherits black
    lineHeight: 1.0, // Word: line=240 (single)
    marginTop: 16,
    marginBottom: 4,
    // MO.03: w:pBdr top+bottom single — border top and bottom lines
  },
  Titulo3: {
    fontFamily: FONTS.normative,
    fontSize: '10pt', // Word: sz=20 → 10pt
    fontWeight: 700, // Word: w:b
    color: DOC_COLORS.body, // Word: no explicit color — inherits black
    lineHeight: 1.0, // Word: line=240 (single)
    marginTop: 12,
    marginBottom: 4,
  },
  Titulo4: {
    fontFamily: FONTS.normative,
    fontSize: '9pt', // Word: inherits sz=18 from Normal
    fontWeight: 700, // Word: w:b
    color: DOC_COLORS.body, // Word: no explicit color — inherits black
    lineHeight: 1.0,
    marginTop: 10,
    marginBottom: 2,
    // MO.03: w:u val="single" — underline (NOT italic)
  },
  Normal: {
    fontFamily: FONTS.normative,
    fontSize: '9pt', // Word: sz=18 → 9pt
    fontWeight: 400,
    color: DOC_COLORS.body,
    lineHeight: 1.5, // Word: line=360 → 1.5×
    textAlign: 'justify', // Word: jc=both
    textIndent: '1.25cm', // Word: firstLine=709twips ≈ 1.25cm
    marginTop: 12, // Word: before=240twips → 12pt
    marginBottom: 12, // Word: after=240twips → 12pt
  },
  NormalNegrita: {
    fontFamily: FONTS.normative,
    fontSize: '9pt', // hereda de Normal
    fontWeight: 700,
    color: DOC_COLORS.body,
    lineHeight: 1.0, // Word: line=240 (single)
    textAlign: 'justify',
    marginTop: 0,
    marginBottom: 0,
  },
  Parrafodelista: {
    fontFamily: FONTS.normative,
    fontSize: '9pt', // hereda de Normal
    fontWeight: 400,
    color: DOC_COLORS.body,
    lineHeight: 1.0, // Word: line=240 (single)
    marginTop: 6, // Word: before=120twips → 6pt
    marginBottom: 6, // Word: after=120twips → 6pt
    paddingLeft: 24,
    listStyleType: 'disc',
  },
  ParrafodelistaNegrita: {
    fontFamily: FONTS.normative,
    fontSize: '9pt',
    fontWeight: 700,
    color: DOC_COLORS.body,
    lineHeight: 1.0,
    marginTop: 6,
    marginBottom: 6,
    paddingLeft: 24,
    listStyleType: 'disc',
  },
  ParrafodelistaNumerada: {
    fontFamily: FONTS.normative,
    fontSize: '9pt',
    fontWeight: 400,
    color: DOC_COLORS.body,
    lineHeight: 1.0,
    marginTop: 6,
    marginBottom: 6,
    paddingLeft: 24,
  },
  TextoTabla: {
    fontFamily: FONTS.normative,
    fontSize: '9pt', // hereda de Normal (sz=18 → 9pt)
    fontWeight: 400,
    color: DOC_COLORS.body,
    lineHeight: 1.0, // Word: line=240 (single)
    textAlign: 'center', // Word: jc=center
    marginTop: 2, // Word: before=40twips → 2pt
    marginBottom: 2, // Word: after=40twips → 2pt
  },
  Descripcion: {
    fontFamily: FONTS.normative,
    fontSize: '8pt', // Word: sz=16 → 8pt
    fontWeight: 400,
    color: '#808080', // Word: color=808080
    lineHeight: 1.0, // Word: line=240 (single)
    fontStyle: 'italic',
    marginTop: 0,
    marginBottom: 10, // Word: after=200twips → 10pt
  },
  NotaNormativa: {
    fontFamily: FONTS.normative,
    fontSize: '9pt',
    fontWeight: 700, // Word: bold
    color: '#0070C0', // Word: color=0070C0
    lineHeight: 1.0,
    fontStyle: 'italic',
    marginTop: 10,
    marginBottom: 10,
  },
};

// ─── Character styles (marks) ───
export interface CharacterStyle {
  fontWeight?: number;
  fontStyle?: string;
  textDecoration?: string;
  color?: string;
}

export const CHARACTER_STYLES: Record<string, CharacterStyle> = {
  NormalCaracterNegrita: { fontWeight: 700 },
  NormalCaracterCursiva: { fontStyle: 'italic' },
  NormalCaracterCursivaNegrita: { fontWeight: 700, fontStyle: 'italic' },
  Azul: { color: DOC_COLORS.blueChar },
  Rojo: { color: DOC_COLORS.redChar },
  AzulCursiva: { color: DOC_COLORS.blueChar, fontStyle: 'italic' },
  RojoCursiva: { color: DOC_COLORS.redChar, fontStyle: 'italic' },
};

// ─── Table styles ───
export const TABLE_STYLES = {
  headerN1: {
    background: DOC_COLORS.tableHeaderBg,
    color: DOC_COLORS.tableHeaderText,
    padding: '8px 12px',
    fontWeight: 700,
    fontSize: '10pt',
    textAlign: 'left' as const,
    border: `1px solid ${DOC_COLORS.tableBorder}`,
  },
  headerN2: {
    background: DOC_COLORS.tableHeaderBgAlt,
    color: DOC_COLORS.tableHeaderText,
    padding: '8px 12px',
    fontWeight: 700,
    fontSize: '10pt',
    textAlign: 'left' as const,
    border: `1px solid ${DOC_COLORS.tableBorder}`,
  },
  cell: {
    padding: '8px 12px',
    fontSize: '10pt',
    color: DOC_COLORS.body,
    border: `1px solid ${DOC_COLORS.tableBorder}`,
  },
  altRowBg: DOC_COLORS.tableAltRowBg,
  tableBorder: `1px solid ${DOC_COLORS.tableBorder}`,
  tableMargin: '16px 0',
} as const;

// ─── Callout / Blockquote style ───
export const CALLOUT_STYLE = {
  fontFamily: FONTS.normative,
  fontSize: '9pt',
  fontWeight: 700,
  fontStyle: 'italic' as const,
  color: DOC_COLORS.calloutText,
  borderLeft: `4px solid ${DOC_COLORS.calloutBorder}`,
  background: DOC_COLORS.calloutBg,
  padding: '14px 18px',
  borderRadius: '0 6px 6px 0',
  margin: '10pt 0',
} as const;

// ─── Page header/footer configuration ───
// logoSrc defaults to static path; overridden at runtime by TenantProvider via getPageHeaderLogo()
let _tenantLogoUrl: string | null = null;

/** Called by TenantProvider to set the tenant logo for document page headers */
export function setTenantLogo(url: string | null) {
  _tenantLogoUrl = url;
}

/** Returns the logo URL to use in document page headers (tenant logo or default) */
export function getPageHeaderLogo(): string {
  return _tenantLogoUrl ?? '/img/Logo-horizontal-Color.png';
}

export const PAGE_HEADER = {
  logoSrc: '/img/Logo-horizontal-Color.png',
  logoHeight: 20,
  fontFamily: FONTS.normative,
  fontSize: 9,
  borderBottom: `1.5px solid ${DOC_COLORS.headerBorderBottom}`,
  mutedColor: DOC_COLORS.pageHeaderMuted,
  accentColor: DOC_COLORS.pageHeaderAccent,
} as const;

export const PAGE_FOOTER = {
  fontFamily: FONTS.normative,
  fontSize: 9,
  color: DOC_COLORS.pageHeaderMuted,
  borderTop: `1px solid ${DOC_COLORS.footerBorderTop}`,
} as const;

// ─── Word ↔ ProseMirror bidirectional mappings ───
export const WORD_TO_PROSEMIRROR: Record<string, { type: string; attrs?: Record<string, any> }> = {
  // Paragraph styles → ProseMirror nodes
  Ttulo1: { type: 'heading', attrs: { level: 1 } },
  Titulo1: { type: 'heading', attrs: { level: 1 } },
  Heading1: { type: 'heading', attrs: { level: 1 } },
  Ttulo2: { type: 'heading', attrs: { level: 2 } },
  Titulo2: { type: 'heading', attrs: { level: 2 } },
  Heading2: { type: 'heading', attrs: { level: 2 } },
  Ttulo3: { type: 'heading', attrs: { level: 3 } },
  Titulo3: { type: 'heading', attrs: { level: 3 } },
  Heading3: { type: 'heading', attrs: { level: 3 } },
  Ttulo4: { type: 'heading', attrs: { level: 4 } },
  Titulo4: { type: 'heading', attrs: { level: 4 } },
  Heading4: { type: 'heading', attrs: { level: 4 } },

  // Lists
  Prrafodelista: { type: 'bulletList' },
  Parrafodelista: { type: 'bulletList' },
  ListParagraph: { type: 'bulletList' },

  // Numbered lists
  ParrafodelistaNumerada: { type: 'orderedList' },
  PrrafodelistaNumerada: { type: 'orderedList' },

  // Normal
  Normal: { type: 'paragraph' },
  NormalWeb: { type: 'paragraph' },

  // Descriptions / notes
  Descripcin: { type: 'paragraph' }, // rendered as figcaption/desc
  Descripcion: { type: 'paragraph' },
  NotaNormativa: { type: 'blockquote' },
};

export const PROSEMIRROR_TO_WORD: Record<string, string> = {
  'heading-1': 'Titulo1',
  'heading-2': 'Titulo2',
  'heading-3': 'Titulo3',
  'heading-4': 'Titulo4',
  paragraph: 'Normal',
  bulletList: 'Parrafodelista',
  orderedList: 'ParrafodelistaNumerada',
  blockquote: 'NotaNormativa',
  table: 'TextoTabla',
};

// ─── Character style mappings (Word rStyle → ProseMirror marks) ───
export const WORD_CHAR_TO_MARKS: Record<string, { type: string; attrs?: Record<string, any> }[]> = {
  NormalCarcterNegrita: [{ type: 'bold' }],
  NormalCaracterNegrita: [{ type: 'bold' }],
  NormalCarcterCursiva: [{ type: 'italic' }],
  NormalCaracterCursiva: [{ type: 'italic' }],
  NormalCarcterCursivaNegrita: [{ type: 'bold' }, { type: 'italic' }],
  NormalCaracterCursivaNegrita: [{ type: 'bold' }, { type: 'italic' }],
  NormalCarcterAzul: [{ type: 'textStyle', attrs: { color: DOC_COLORS.blueChar } }],
  NormalCaracterAzul: [{ type: 'textStyle', attrs: { color: DOC_COLORS.blueChar } }],
  NormalCarcterRojo: [{ type: 'textStyle', attrs: { color: DOC_COLORS.redChar } }],
  NormalCaracterRojo: [{ type: 'textStyle', attrs: { color: DOC_COLORS.redChar } }],
};

// ─── TOC detection styles (used for import filtering) ───
export const TOC_STYLES = new Set([
  'TtuloTDC',
  'TtuloTDC1',
  'TDC1',
  'TDC2',
  'TDC3',
  'TOCHeading',
  'TOC1',
  'TOC2',
  'TOC3',
]);

// ─── Allowed editor styles (for toolbar dropdown) ───
export const EDITOR_PARAGRAPH_OPTIONS = [
  { value: 'h1', label: 'Titulo1', group: 'Encabezados' },
  { value: 'h2', label: 'Titulo2', group: 'Encabezados' },
  { value: 'h3', label: 'Titulo3', group: 'Encabezados' },
  { value: 'h4', label: 'Titulo4', group: 'Encabezados' },
  { value: 'p', label: 'Normal', group: 'Parrafo' },
  { value: 'p-bold', label: 'NormalNegrita', group: 'Parrafo' },
  { value: 'list-bullet', label: 'Parrafodelista', group: 'Listas' },
  { value: 'list-bullet-bold', label: 'Parrafodelista Negrita', group: 'Listas' },
  { value: 'list-num', label: 'Parrafodelista Numerada', group: 'Listas' },
  { value: 'table-text', label: 'TextoTabla', group: 'Tabla' },
  { value: 'table-desc', label: 'Descripcion', group: 'Tabla' },
] as const;

export const EDITOR_CHARACTER_OPTIONS = [
  { value: 'char-bold', label: 'NormalCaracterNegrita', group: 'Caracter' },
  { value: 'char-italic', label: 'Cursiva', group: 'Caracter' },
  { value: 'char-bold-italic', label: 'CursivaNegrita', group: 'Caracter' },
  { value: 'char-blue', label: 'Azul', group: 'Caracter' },
  { value: 'char-red', label: 'Rojo', group: 'Caracter' },
  { value: 'char-blue-italic', label: 'AzulCursiva', group: 'Caracter' },
  { value: 'char-red-italic', label: 'RojoCursiva', group: 'Caracter' },
] as const;

// ─── Generate CSS for .viewer-content ───
export function generateViewerCSS(): string {
  const s = PARAGRAPH_STYLES;
  const t = TABLE_STYLES;
  const c = CALLOUT_STYLE;

  return `
    .viewer-content { font-family: ${FONTS.normative}; }
    .viewer-content h1 {
      font-family: ${s.Titulo1.fontFamily};
      font-size: ${s.Titulo1.fontSize}; font-weight: ${s.Titulo1.fontWeight}; color: ${s.Titulo1.color};
      line-height: ${s.Titulo1.lineHeight};
      background: ${DOC_COLORS.h1}; padding: 8px 12px; text-transform: uppercase;
      margin: ${s.Titulo1.marginTop}px 0 ${s.Titulo1.marginBottom}px;
    }
    .viewer-content h1:first-child { margin-top: 0; }
    .viewer-content h2 {
      font-family: ${s.Titulo2.fontFamily};
      font-size: ${s.Titulo2.fontSize}; font-weight: ${s.Titulo2.fontWeight}; color: ${s.Titulo2.color};
      line-height: ${s.Titulo2.lineHeight};
      border-top: 1px solid ${DOC_COLORS.body}; border-bottom: 1px solid ${DOC_COLORS.body};
      padding: 4px 0;
      margin: ${s.Titulo2.marginTop}px 0 ${s.Titulo2.marginBottom}px;
    }
    .viewer-content h3 {
      font-family: ${s.Titulo3.fontFamily};
      font-size: ${s.Titulo3.fontSize}; font-weight: ${s.Titulo3.fontWeight}; color: ${s.Titulo3.color};
      line-height: ${s.Titulo3.lineHeight};
      margin: ${s.Titulo3.marginTop}px 0 ${s.Titulo3.marginBottom}px;
    }
    .viewer-content h4 {
      font-family: ${s.Titulo4.fontFamily};
      font-size: ${s.Titulo4.fontSize}; font-weight: ${s.Titulo4.fontWeight}; color: ${s.Titulo4.color};
      line-height: ${s.Titulo4.lineHeight}; text-decoration: underline;
      margin: ${s.Titulo4.marginTop}px 0 ${s.Titulo4.marginBottom}px;
    }
    .viewer-content p {
      font-family: ${s.Normal.fontFamily};
      font-size: ${s.Normal.fontSize}; color: ${s.Normal.color}; text-align: ${s.Normal.textAlign};
      line-height: ${s.Normal.lineHeight}; text-indent: ${s.Normal.textIndent};
      margin: ${s.Normal.marginTop}px 0 ${s.Normal.marginBottom}px;
    }
    .viewer-content ul {
      font-family: ${s.Parrafodelista.fontFamily};
      font-size: ${s.Parrafodelista.fontSize}; color: ${s.Parrafodelista.color}; line-height: ${s.Parrafodelista.lineHeight};
      margin: ${s.Parrafodelista.marginTop}px 0 ${s.Parrafodelista.marginBottom}px; padding-left: ${s.Parrafodelista.paddingLeft}px; list-style-type: ${s.Parrafodelista.listStyleType};
    }
    .viewer-content ul li { margin-bottom: 2px; }
    .viewer-content ol {
      font-family: ${s.ParrafodelistaNumerada.fontFamily};
      font-size: ${s.ParrafodelistaNumerada.fontSize}; color: ${s.ParrafodelistaNumerada.color}; line-height: ${s.ParrafodelistaNumerada.lineHeight};
      margin: ${s.ParrafodelistaNumerada.marginTop}px 0 ${s.ParrafodelistaNumerada.marginBottom}px; padding-left: ${s.ParrafodelistaNumerada.paddingLeft}px;
    }
    .viewer-content ol li { margin-bottom: 2px; }
    .viewer-content strong { font-weight: 700; }
    .viewer-content strong em { color: ${DOC_COLORS.boldItalicColor}; font-weight: 700; font-style: italic; }
    .viewer-content em { font-style: italic; }
    .viewer-content .dtbl { width: 100%; border-collapse: collapse; margin: ${t.tableMargin}; font-family: ${FONTS.normative}; font-size: ${t.cell.fontSize}; border: ${t.tableBorder}; }
    .viewer-content .dtbl th {
      padding: ${t.headerN1.padding}; font-weight: ${t.headerN1.fontWeight}; font-size: ${t.headerN1.fontSize};
      text-align: center; vertical-align: middle; color: ${t.headerN1.color}; border: ${t.headerN1.border};
    }
    .viewer-content .dtbl td { padding: ${t.cell.padding}; font-size: ${t.cell.fontSize}; color: ${t.cell.color}; border: ${t.cell.border}; }
    .viewer-content .dtbl tbody tr:nth-child(even) td,
    .viewer-content .dtbl tbody tr:nth-child(even) th { background: ${t.altRowBg}; }
    .viewer-content .dtbl tbody tr:first-child th { background: ${t.headerN1.background || DOC_COLORS.tableHeaderBg}; color: ${t.headerN1.color}; }
    .viewer-content .dtbl th p { margin: 0; line-height: 1.15; color: inherit; text-align: inherit; }
    .viewer-content .dtbl td p { margin: 0; line-height: 1.15; }
    .viewer-content .callout {
      font-family: ${c.fontFamily};
      font-size: ${c.fontSize}; font-weight: ${c.fontWeight}; font-style: ${c.fontStyle}; color: ${c.color};
      border-left: ${c.borderLeft}; background: ${c.background};
      padding: ${c.padding}; border-radius: ${c.borderRadius}; margin: ${c.margin};
    }
    .viewer-content table:not(.dtbl) { width: 100%; border-collapse: collapse; margin: ${t.tableMargin}; font-family: ${FONTS.normative}; font-size: ${t.cell.fontSize}; border: ${t.tableBorder}; }
    .viewer-content table:not(.dtbl) th {
      padding: ${t.headerN2.padding}; font-weight: ${t.headerN2.fontWeight}; font-size: ${t.headerN2.fontSize};
      text-align: center; vertical-align: middle; color: ${t.headerN2.color}; background: ${t.headerN2.background}; border: ${t.headerN2.border};
    }
    .viewer-content table:not(.dtbl) td { padding: ${t.cell.padding}; font-size: ${t.cell.fontSize}; color: ${t.cell.color}; border: ${t.cell.border}; }
    .viewer-content table:not(.dtbl) tbody tr:nth-child(even) td,
    .viewer-content table:not(.dtbl) tbody tr:nth-child(even) th { background: ${t.altRowBg}; }
    .viewer-content table:not(.dtbl) tbody tr:first-child th { background: ${t.headerN2.background}; color: ${t.headerN2.color}; }
    .viewer-content table:not(.dtbl) th p { margin: 0; line-height: 1.15; color: inherit; text-align: inherit; }
    .viewer-content table:not(.dtbl) td p { margin: 0; line-height: 1.15; }
    .viewer-content figcaption, .viewer-content .desc {
      font-family: ${s.Descripcion.fontFamily};
      font-size: ${s.Descripcion.fontSize}; font-style: ${s.Descripcion.fontStyle}; color: ${s.Descripcion.color}; margin: ${s.Descripcion.marginTop}px 0 ${s.Descripcion.marginBottom}px;
    }
    .viewer-content hr { border: none; border-top: 1px solid ${DOC_COLORS.hr}; margin: 24px 0; }

    /* ─── pStyle overrides (from Word import attrs) ─── */
    .viewer-content p.ps-NormalNegrita {
      font-weight: 700; text-indent: 0; line-height: ${s.NormalNegrita.lineHeight}; margin: 0;
    }
    .viewer-content p.ps-Descripcion {
      font-size: ${s.Descripcion.fontSize}; font-style: ${s.Descripcion.fontStyle};
      color: ${s.Descripcion.color}; text-indent: 0; line-height: ${s.Descripcion.lineHeight};
      margin: ${s.Descripcion.marginTop}px 0 ${s.Descripcion.marginBottom}px;
    }
    .viewer-content p.ps-NotaNormativa {
      font-style: italic; font-weight: ${s.NotaNormativa.fontWeight}; color: ${s.NotaNormativa.color};
      text-indent: 0; line-height: ${s.NotaNormativa.lineHeight};
      border-left: 4px solid ${DOC_COLORS.calloutBorder}; background: ${DOC_COLORS.calloutBg};
      padding: 10px 16px; border-radius: 0 4px 4px 0; margin: ${s.NotaNormativa.marginTop}px 0 ${s.NotaNormativa.marginBottom}px;
    }

    /* ─── Table cell paragraphs: reset Normal indent/margin ─── */
    .viewer-content .dtbl td p, .viewer-content table:not(.dtbl) td p { text-indent: 0; }
    .viewer-content ul p, .viewer-content ol p { text-indent: 0; margin: 0; }
  `;
}
